import React, { useState, useContext } from "react";
import { useQuery } from "@apollo/client";
import { NotificationRulesTable, CreateNotificationForm } from "@design-system";
import { Layout, Modal } from "antd";
import { UsersSelectContainer } from "../users/users-select-container";
import { FEE_EARNER_LEVELS_QUERY, ORGANISATIONAL_UNITS_QUERY } from "../../graphql/query";
import {
  NotificationRule,
  CreateNotificationRuleMutation,
  CreateNotificationRuleMutationVariables,
  UpdateNotificationRuleMutation,
  UpdateNotificationRuleMutationVariables,
  UpdateActiveNotificationRulesMutation,
  UpdateActiveNotificationRulesMutationVariables,
  DeleteNotificationRulesMutation,
  DeleteNotificationRulesMutationVariables,
  NotificationRulesQuery,
} from "../../gql/graphql";
import { MultiSelectClientContainer } from "./multi-select-client-container";
import { MultiSelectMatterContainer } from "./multi-select-matter-container";

import styles from "./configure-notifications-container.css";

const { content: contentClassName } = styles as any;

import { NOTIFICATION_RULE_QUERY } from "../../graphql/query/notification-rule-query";
import {
  CREATE_NOTIFICATION_RULE,
  UPDATE_NOTIFICATION_RULE,
  UPDATE_ACTIVE_NOTIFICATION_RULES,
  DELETE_NOTIFICATION_RULES,
} from "../../graphql/mutation";
import { FindOrganisationalUnits } from "../../graphql/__generated_types__/FindOrganisationalUnits";
import { FindFeeEarnerLevels } from "../../graphql/__generated_types__/FindFeeEarnerLevels";
import { useMutation, useSettings } from "../../hooks";
import { GlobalContext } from "../../context/global-context";

export const ConfigureNotificationsContainer = () => {
  const { settings, loading } = useSettings();
  const currentUserInfo = useContext(GlobalContext);
  const currentUserId = currentUserInfo?.currentUser?.userId ?? -1;

  const { data: organisationalUnitData } = useQuery<FindOrganisationalUnits>(
    ORGANISATIONAL_UNITS_QUERY
  );

  const { data: feeEarnerLevelData } = useQuery<FindFeeEarnerLevels>(FEE_EARNER_LEVELS_QUERY);
  const { data: notificationRuleData } = useQuery<NotificationRulesQuery>(NOTIFICATION_RULE_QUERY, {
    skip: !currentUserId,
    variables: {
      data: {
        userId: currentUserId,
      },
    },
  });
  const [createNotificationRule] = useMutation<
    CreateNotificationRuleMutation,
    CreateNotificationRuleMutationVariables
  >(CREATE_NOTIFICATION_RULE, {
    update: (cache, mutationResult) => {
      const newNotificationRule = mutationResult?.data?.createNotificationRule;
      const data: any = cache.readQuery({
        query: NOTIFICATION_RULE_QUERY,
        variables: { data: { userId: currentUserId } },
      });
      cache.writeQuery({
        query: NOTIFICATION_RULE_QUERY,
        data: { notificationRules: [...data.notificationRules, newNotificationRule] },
        variables: { data: { userId: currentUserId } },
      });
    },
  });
  const [updateNotificationRule] = useMutation<
    UpdateNotificationRuleMutation,
    UpdateNotificationRuleMutationVariables
  >(UPDATE_NOTIFICATION_RULE);
  const [updateActiveNotificationRules] = useMutation<
    UpdateActiveNotificationRulesMutation,
    UpdateActiveNotificationRulesMutationVariables
  >(UPDATE_ACTIVE_NOTIFICATION_RULES, {
    update: (cache, mutationResult) => {
      const updatedNotificationRuleIds =
        mutationResult?.data?.updateActiveNotificationRules.updatedIds ?? [];
      const updatedActiveStatus = mutationResult?.data?.updateActiveNotificationRules.active;
      const data: any = cache.readQuery({
        query: NOTIFICATION_RULE_QUERY,
        variables: { data: { userId: currentUserId } },
      });
      cache.writeQuery({
        query: NOTIFICATION_RULE_QUERY,
        variables: { data: { userId: currentUserId } },
        data: {
          notificationRules: data.notificationRules.map((n: any) => {
            if (updatedNotificationRuleIds.includes(n.id)) {
              return {
                ...n,
                active: updatedActiveStatus,
              };
            }
            return n;
          }),
        },
      });
    },
  });
  const [deleteNotificationRules] = useMutation<
    DeleteNotificationRulesMutation,
    DeleteNotificationRulesMutationVariables
  >(DELETE_NOTIFICATION_RULES, {
    update: (cache, mutationResult) => {
      const deletedNotificationRuleIds =
        mutationResult?.data?.deleteNotificationRules?.notificationRuleIds ?? [];
      const data: any = cache.readQuery({
        query: NOTIFICATION_RULE_QUERY,
        variables: { data: { userId: currentUserId } },
      });
      cache.writeQuery({
        query: NOTIFICATION_RULE_QUERY,
        variables: { data: { userId: currentUserId } },
        data: {
          notificationRules: data.notificationRules.filter(
            (n: any) => !deletedNotificationRuleIds.includes(n.id)
          ),
        },
      });
    },
  });
  const [displayCreateModel, setDisplayCreateModel] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState<number | null>(null);
  const practiceAreas = organisationalUnitData?.organisationalUnits || [];

  const roles = feeEarnerLevelData?.feeEarnerLevels.records || [];
  const notificationRules = notificationRuleData?.notificationRules || [];
  const sideBarNotificationRule =
    (expandedDetails && notificationRules.find((n: any) => n.id === expandedDetails)) || undefined;

  if (loading) {
    return null;
  }

  return (
    <Layout>
      <Modal open={displayCreateModel} closable={false} footer={null} width="1064px">
        <CreateNotificationForm
          currency={settings.currency}
          heading="Create Alert"
          onCancel={() => setDisplayCreateModel(false)}
          onComplete={(values: NotificationRule) => {
            const currencyCode =
              (values.proposalFeeThreshold ?? 0) > 0 ||
              ((values.thresholdFilter ?? 0) > 0 && (values as any).sharedCurrencyCode)
                ? (values as any).sharedCurrencyCode
                : null; //TODO: The values type of NotificationRule is not correct here

            setDisplayCreateModel(false);
            createNotificationRule({
              variables: {
                data: {
                  currency: {
                    ...(currencyCode && {
                      connect: {
                        code: currencyCode, //TODO: The values type of NotificationRule is not correct here
                      },
                    }),
                  },
                  accruedBudgetThreshold: values.accruedBudgetThreshold,
                  activeGpmThreshold: values.activeGpmThreshold,
                  proposalFeeThreshold: values.proposalFeeThreshold,
                  proposalGpmThreshold: values.proposalGpmThreshold,
                  name: values.name,
                  active: values.active,
                  triggerBooleanType: values.triggerBooleanType,
                  thresholdBooleanType: values.thresholdBooleanType,
                  thresholdFilter: values.thresholdFilter,
                  ...((values.triggerOrganisationalUnits?.length ?? 0) > 0 && {
                    triggerOrganisationalUnits: {
                      createMany: {
                        data: values.triggerOrganisationalUnits!.map((u) => ({
                          organisationalUnitId: u.id,
                        })),
                      },
                    },
                  }),
                  ...((values.notifyOrganisationalUnits?.length ?? 0) > 0 && {
                    notifyOrganisationalUnits: {
                      createMany: {
                        data: values.notifyOrganisationalUnits!.map((u) => ({
                          organisationalUnitId: u.id,
                        })),
                      },
                    },
                  }),
                  ...((values.notifyUsers?.length ?? 0) > 0 && {
                    notifyUsers: {
                      createMany: { data: values.notifyUsers!.map((u) => ({ userId: u.id })) },
                    },
                  }),
                  ...((values.notifyFeeEarnerLevels?.length ?? 0) > 0 && {
                    notifyFeeEarnerLevels: {
                      createMany: {
                        data: values.notifyFeeEarnerLevels!.map((u) => ({
                          feeEarnerLevelId: u.id,
                        })),
                      },
                    },
                  }),
                  ...((values.triggerFeeEarnerLevels?.length ?? 0) > 0 && {
                    triggerFeeEarnerLevels: {
                      createMany: {
                        data: values.triggerFeeEarnerLevels!.map((u) => ({
                          feeEarnerLevelId: u.id,
                        })),
                      },
                    },
                  }),
                  ...((values.triggerClients?.length ?? 0) > 0 && {
                    triggerClients: {
                      createMany: {
                        data: values.triggerClients!.map((x: any) => {
                          return {
                            clientId: x.id,
                          };
                        }),
                      },
                    },
                  }),
                  ...((values.triggerUsers?.length ?? 0) > 0 && {
                    triggerUsers: {
                      createMany: {
                        data: values.triggerUsers!.map((u) => ({ userId: u.id })),
                      },
                    },
                  }),
                  ...((values.triggerMatters?.length ?? 0) > 0 && {
                    triggerMatters: {
                      createMany: {
                        data: values.triggerMatters!.map((x) => {
                          return {
                            matterId: x.id,
                          };
                        }),
                      },
                    },
                  }),
                },
              },
            });
          }}
          practiceAreas={practiceAreas}
          roles={roles}
          individualsNotifyInputRender={() => (
            <UsersSelectContainer
              requiredUsers={sideBarNotificationRule?.notifyUsers || undefined}
            />
          )}
          individualsCreatorInputRender={() => (
            <UsersSelectContainer
              requiredUsers={sideBarNotificationRule?.triggerUsers || undefined}
            />
          )}
          clientsRender={() => <MultiSelectClientContainer canCreateClient={false} />}
          mattersRender={() => <MultiSelectMatterContainer />}
        />
      </Modal>
      <Modal open={Boolean(expandedDetails)} closable={false} footer={null} width="1064px">
        <CreateNotificationForm
          currency={sideBarNotificationRule?.currency ?? settings.currency}
          key={expandedDetails}
          heading="Edit Alert"
          onCancel={() => setExpandedDetails(null)}
          onComplete={(values: NotificationRule) => {
            setExpandedDetails(null);
            updateNotificationRule({
              variables: {
                data: {
                  name: values.name,
                  id: sideBarNotificationRule?.id!,
                  active: values.active,
                  accruedBudgetThreshold: values.accruedBudgetThreshold,
                  activeGpmThreshold: values.activeGpmThreshold,
                  proposalFeeThreshold: values.proposalFeeThreshold,
                  proposalGpmThreshold: values.proposalGpmThreshold,
                  notifyUsers: values.notifyUsers?.map((x) => x.id),
                  notifyFeeEarnerLevels: values.notifyFeeEarnerLevels?.map((x) => x.id),
                  triggerFeeEarnerLevels: values.triggerFeeEarnerLevels?.map((x) => x.id),
                  triggerOrganisationalUnits: values.triggerOrganisationalUnits?.map((x) => x.id),
                  notifyOrganisationalUnits: values.notifyOrganisationalUnits?.map((x) => x.id),
                  triggerUsers: values.triggerUsers?.map((x) => x.id),
                  triggerClients: values.triggerClients?.map((x) => x.id),
                  triggerMatters: values.triggerMatters?.map((x) => x.id),
                  triggerBooleanType: values.triggerBooleanType,
                  thresholdBooleanType: values.thresholdBooleanType,
                  thresholdFilter: values.thresholdFilter,
                  currencyCode:
                    (values.proposalFeeThreshold ?? 0) > 0 || (values.thresholdFilter ?? 0) > 0
                      ? (values as any).sharedCurrencyCode
                      : null,
                },
              },
            });
          }}
          practiceAreas={practiceAreas}
          roles={roles}
          initialValues={sideBarNotificationRule as Partial<NotificationRule>}
          submitButtonText="Update"
          individualsNotifyInputRender={() => (
            <UsersSelectContainer requiredUsers={sideBarNotificationRule?.notifyUsers ?? []} />
          )}
          individualsCreatorInputRender={() => (
            <UsersSelectContainer requiredUsers={sideBarNotificationRule?.triggerUsers ?? []} />
          )}
          clientsRender={() => (
            <MultiSelectClientContainer
              requiredClients={sideBarNotificationRule?.triggerClients ?? []}
              canCreateClient={false}
            />
          )}
          mattersRender={() => (
            <MultiSelectMatterContainer
              requiredMatters={sideBarNotificationRule?.triggerMatters ?? []}
            />
          )}
        />
      </Modal>
      <Layout.Content className={contentClassName}>
        <NotificationRulesTable
          loading={loading}
          notificationRules={notificationRules as NotificationRule[]}
          onDeleteNotificationRules={(notificationRuleIds) => {
            deleteNotificationRules({ variables: { notificationRuleIds } });
            if (expandedDetails && notificationRuleIds.includes(expandedDetails))
              setExpandedDetails(null);
          }}
          onUpdateActiveNotificationRules={(notificationRuleIds, active) => {
            updateActiveNotificationRules({
              variables: {
                data: {
                  notificationRuleIds,
                  active,
                },
              },
            });
          }}
          onClickNotificationRule={setExpandedDetails}
          onClickCreateNotificationButton={() => setDisplayCreateModel(true)}
        />
      </Layout.Content>
    </Layout>
  );
};
